import React, { useState, useEffect, useRef } from "react";
import { Line } from "react-chartjs-2";
import { GaugeComponent } from "react-gauge-component";
import Buy from "../../devdata/assets/mmi/b.svg";
import Sell from "../../devdata/assets/mmi/s.svg";
import Neutral from "../../devdata/assets/mmi/n.svg";
import StrongBuy from "../../devdata/assets/mmi/sb.svg";
import StrongSell from "../../devdata/assets/mmi/ss.svg";
import Needle from "../../devdata/assets/needle.svg";
import { Tag } from "antd";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Subhai } from "../";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// If needed, you can keep these offsets and positions for desktop
const NEEDLE_ROTATION_OFFSET = {
  "Strong Sell": { desktop: 0 },
  Sell: { desktop: 0 },
  Neutral: { desktop: 0 },
  Buy: { desktop: 0 },
  "Strong Buy": { desktop: 0 },
};

const NEEDLE_POSITIONS = {
  "Strong Sell": {
    desktop: { top: 247, left: 220 },
  },
  Sell: {
    desktop: { top: 247, left: 220 },
  },
  Neutral: {
    desktop: { top: 247, left: 220 },
  },
  Buy: {
    desktop: { top: 247, left: 220 },
  },
  "Strong Buy": {
    desktop: { top: 247, left: 220 },
  },
};

// Custom hook to track the width of a component using ResizeObserver.
const useComponentWidth = (ref) => {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0]) {
        setWidth(entries[0].contentRect.width);
      }
    });
    resizeObserver.observe(ref.current);
    return () => resizeObserver.disconnect();
  }, [ref]);
  return width;
};

// Linear interpolation helper
const lerp = (a, b, t) => a + (b - a) * t;

// Interpolate the needle rotation based solely on currentMMI.
// The intervals and target angles are defined as follows:
//   mmi <= 50: returns 7°
//   50 <= mmi <= 65: from 7° to 72°
//   65 <= mmi <= 85: from 72° to 145°
//   85 <= mmi <= 100: from 145° to 219°
//   100 <= mmi <= 115: from 219° to 311°
//   mmi > 115: clamp at 311°
const getNeedleRotation = (mmi) => {
  if (mmi <= 50) return "7deg";
  else if (mmi <= 65) {
    const t = (mmi - 50) / (65 - 50);
    return `${lerp(7, 66, t)}deg`;
  } else if (mmi <= 85) {
    const t = (mmi - 65) / (85 - 65);
    return `${lerp(66, 127, t)}deg`;
  } else if (mmi <= 100) {
    const t = (mmi - 85) / (100 - 85);
    return `${lerp(127, 185, t)}deg`;
  } else if (mmi <= 115) {
    const t = (mmi - 100) / (115 - 100);
    return `${lerp(185, 244.5, t)}deg`;
  } else if (mmi <= 130) {
    const t = (mmi - 115) / (130 - 115);
    return `${lerp(244.5, 306, t)}deg`;
  }
};

const getNeedlePosition = (mood) => {
  const moodPositions = NEEDLE_POSITIONS[mood];
  return moodPositions ? moodPositions.desktop : { top: 0, left: 0 };
};

const getMoodImage = (mood) => {
  switch (mood) {
    case "Strong Buy":
      return StrongBuy;
    case "Buy":
      return Buy;
    case "Neutral":
      return Neutral;
    case "Sell":
      return Sell;
    case "Strong Sell":
      return StrongSell;
    default:
      return StrongSell;
  }
};

const MMI = ({ onDashboard = true }) => {
  const [currentMMI, setCurrentMMI] = useState(0);
  const [currentMood, setCurrentMood] = useState("Strong Sell"); // Default mood
  const [refresh, setrefresh] = useState(false);

  // Create a ref for the container whose width we'll measure.
  const containerRef = useRef(null);
  const containerWidth = useComponentWidth(containerRef);

  // Compute the interpolation factor t based on containerWidth.
  // When containerWidth is 300px, t=0; when 600px, t=1.
  const t = Math.min(1, Math.max(0, (containerWidth - 300) / (600 - 300)));
  // Use lerp to interpolate the scale between 0.6 and 1.2.
  const scale = lerp(0.6, 1.2, t);

  useEffect(() => {
    setrefresh(true);
  }, []);

  useEffect(() => {
    const oiDatas = sessionStorage.getItem("oidata");
    if (oiDatas) {
      setCurrentMMI(Number(oiDatas));
      // setCurrentMMI(130);
    }
    const interval = setInterval(() => {
      const oiDatas = sessionStorage.getItem("oidata");
      if (oiDatas) {
        setCurrentMMI(Number(oiDatas));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [refresh]);

  useEffect(() => {
    // Determine mood based on currentMMI
    // Here we use the following ranges:
    // mmi <= 65: "Strong Sell"
    // 65 < mmi <= 85: "Sell"
    // 85 < mmi <= 100: "Neutral"
    // 100 < mmi <= 115: "Buy"
    // mmi > 115: "Strong Buy"
    if (currentMMI <= 65) setCurrentMood("Strong Sell");
    else if (currentMMI <= 85) setCurrentMood("Sell");
    else if (currentMMI <= 100) setCurrentMood("Neutral");
    else if (currentMMI <= 115) setCurrentMood("Buy");
    else setCurrentMood("Strong Buy");
  }, [currentMMI]);

  return (
    <div
      ref={containerRef}
      className="bg-darkMode-layer1 rounded-xl p-4 relative"
    >
      <div className="flex items-start justify-between text-lg font-medium text-darkMode-text ">
        <span>Market Mood Index</span>
        <Tag className="bg-darkMode-layer2 text-darkMode-subText rounded-full  border-none">
          <span className="text-xs">Updated just now</span>
        </Tag>
      </div>
      <div className="mt-4 relative items-center justify-center flex px-4 py-4">
        <Subhai />
        <div
          style={{
            transform: `scale(${scale})`,
            transition: "transform 0.0s ease",
          }}
        >
          <div
            className="flex flex-col items-center justify-center relative"
            style={{ width: 500 }}
          >
            <img
              src={Needle}
              alt="needle"
              style={{
                position: "absolute",
                ...getNeedlePosition(currentMood),
                // Use the interpolated rotation from getNeedleRotation based on currentMMI
                transform: `rotate(${getNeedleRotation(currentMMI)})`,
                transformOrigin: "31px 4px",
              }}
            />
            <img
              src={getMoodImage(currentMood)}
              alt={currentMood}
              width={500}
              className="h-auto"
            />

            <div
              className="absolute flex flex-col items-center text-xl font-semibold text-darkMode-text"
              style={{
                bottom: "18%",
                left: "49%",
                transform: `translateX(-35%)`,
                color: "white",
              }}
            >
              <span>{currentMMI.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MMI;
